
// client/src/components/messages/MessageThreadModal.jsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useMessagesUI } from '../../context/MessagesContext';
import { 
  getThreads, 
  getThread, 
  sendMessage, 
  markThreadAsRead,
  searchUsers,
  createThread 
} from '../../services/messagesApi';
import { getSessionLocal } from '../../services/sessionLocal';
import './MessageThreadModal.css';

export default function MessageThreadModal({ tenantId: propTenantId }) {
  const { isOpen, closeMessages, activeThreadId } = useMessagesUI();
  
  // Get session data
  const session = useMemo(() => getSessionLocal(), []);
  const tenantId = propTenantId || session.tenantId;
  const myUserId = useMemo(() => {
    const me = session?.userId || session?.userid;
    try {
      const stored = localStorage.getItem('me');
      if (stored) {
        const parsed = JSON.parse(stored);
        return Number(parsed?.id || parsed?.userid || parsed?.userId || me || 0);
      }
    } catch {}
    return Number(me || 0);
  }, [session]);
  
  // Thread list state
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [threadsError, setThreadsError] = useState(null);
  
  // Active thread state
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  
  // Compose state
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  
  // New thread state
  const [showNewThread, setShowNewThread] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [creatingThread, setCreatingThread] = useState(false);

  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      
      if (isNearBottom || messages.length === 1) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages]);

  // Load threads when modal opens
  useEffect(() => {
    if (!isOpen) return;
    loadThreads();
    
    // If activeThreadId is provided, open that thread
    if (activeThreadId) {
      const thread = threads.find(t => t.id === activeThreadId);
      if (thread) {
        openThread(thread);
      }
    }
  }, [isOpen, activeThreadId]);

  // Load threads
  const loadThreads = async () => {
    if (!tenantId) {
      setThreadsError('No tenant ID available');
      return;
    }

    setLoadingThreads(true);
    setThreadsError(null);
    
    try {
      const result = await getThreads({ tenantId });
      const threadList = result?.threads || result || [];
      setThreads(Array.isArray(threadList) ? threadList : []);
    } catch (error) {
      console.error('Failed to load threads:', error);
      setThreadsError(error.message || 'Failed to load conversations');
      setThreads([]);
    } finally {
      setLoadingThreads(false);
    }
  };

  // Open a thread
  const openThread = async (thread) => {
    setSelectedThread(thread);
    setShowNewThread(false);
    setLoadingMessages(true);
    setMessagesError(null);
    setMessages([]);
    
    try {
      const result = await getThread(thread.id, { tenantId });
      const threadData = result?.thread || result;
      const messageList = threadData?.messages || [];
      setMessages(Array.isArray(messageList) ? messageList : []);
      
      // Mark as read
      try {
        await markThreadAsRead(thread.id, { tenantId });
        
        // Update thread list to reflect read status
        setThreads(prev => 
          prev.map(t => t.id === thread.id ? { ...t, unreadCount: 0, unread_count: 0 } : t)
        );
      } catch (readError) {
        console.warn('Failed to mark as read:', readError);
      }
    } catch (error) {
      console.error('Failed to load thread:', error);
      setMessagesError(error.message || 'Failed to load messages');
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedThread?.id || sending) return;
    
    setSending(true);
    const text = messageText.trim();
    setMessageText('');
    
    // Optimistically add message
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      text,
      senderId: myUserId,
      sender_id: myUserId,
      createdAt: new Date().toISOString(),
      created_at: new Date().toISOString(),
      isOptimistic: true
    };
    setMessages(prev => [...prev, optimisticMessage]);
    
    try {
      const result = await sendMessage(selectedThread.id, text, { tenantId });
      const newMessage = result?.message || result;
      
      if (newMessage) {
        // Replace optimistic message with real one
        setMessages(prev => 
          prev.map(m => m.id === optimisticMessage.id ? newMessage : m)
        );
        
        // Update thread list preview
        setThreads(prev =>
          prev.map(t => t.id === selectedThread.id ? {
            ...t,
            lastMessage: text,
            last_message: text,
            lastAt: newMessage.createdAt || newMessage.created_at,
            last_at: newMessage.createdAt || newMessage.created_at
          } : t)
        );
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessageText(text); // Restore message on error
      // Remove optimistic message
      setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
    } finally {
      setSending(false);
    }
  };

  // Search users with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery && userSearchQuery.trim().length >= 2) {
        handleUserSearch(userSearchQuery);
      } else {
        setUserSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  const handleUserSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setUserSearchResults([]);
      return;
    }
    
    setSearchingUsers(true);
    try {
      const result = await searchUsers(query, { tenantId });
      const users = result?.users || [];
      // Filter out current user
      const filtered = users.filter(u => {
        const uid = Number(u?.id || u?.userid || u?.userId || 0);
        return uid !== myUserId && uid > 0;
      });
      setUserSearchResults(filtered);
    } catch (error) {
      console.error('User search failed:', error);
      setUserSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Toggle user selection
  const toggleUserSelection = (user) => {
    const uid = Number(user?.id || user?.userid || user?.userId || 0);
    setSelectedUsers(prev => {
      const exists = prev.find(u => {
        const existingId = Number(u?.id || u?.userid || u?.userId || 0);
        return existingId === uid;
      });
      if (exists) {
        return prev.filter(u => {
          const existingId = Number(u?.id || u?.userid || u?.userId || 0);
          return existingId !== uid;
        });
      } else {
        return [...prev, user];
      }
    });
  };

  // Create new thread
  const handleCreateThread = async () => {
    if (selectedUsers.length === 0 || creatingThread) return;
    
    setCreatingThread(true);
    try {
      const participantIds = selectedUsers.map(u => 
        Number(u?.id || u?.userid || u?.userId || 0)
      ).filter(id => id > 0);
      
      const result = await createThread(participantIds, { tenantId });
      const newThread = result?.thread || result;
      
      if (newThread?.id) {
        // Add to thread list
        setThreads(prev => [newThread, ...prev]);
        
        // Open the new thread
        setSelectedThread(newThread);
        setMessages([]);
        setShowNewThread(false);
        setSelectedUsers([]);
        setUserSearchQuery('');
        setUserSearchResults([]);
      }
    } catch (error) {
      console.error('Failed to create thread:', error);
      alert('Failed to create conversation: ' + (error.message || 'Unknown error'));
    } finally {
      setCreatingThread(false);
    }
  };

  // Back to thread list
  const backToList = () => {
    setSelectedThread(null);
    setMessages([]);
    setShowNewThread(false);
    setSelectedUsers([]);
    setUserSearchQuery('');
    setUserSearchResults([]);
  };

  // Close modal
  const handleClose = () => {
    backToList();
    closeMessages();
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Format thread participant names (prefer thread.title for system threads like tasks)
  const getThreadParticipantNames = (thread) => {
    if (thread?.title) return thread.title;

    const participants = thread?.participants || [];
    const otherParticipants = participants.filter(p => {
      const pid = Number(p?.id || p?.userid || p?.userId || 0);
      return pid !== myUserId;
    });

    if (otherParticipants.length === 0) return 'Conversation';

    return otherParticipants
      .map(p => p?.displayName || p?.username || p?.email || 'Unknown')
      .join(', ');
  };

  // Format message timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 minute
    if (diff < 60000) return 'Just now';
    
    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes}m ago`;
    }
    
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
    
    // Today
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // This week
    if (diff < 604800000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    
    // Older
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <div className="messages-modal-backdrop" onClick={handleBackdropClick}>
      <div className="messages-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="messages-modal__header">
          <div className="d-flex align-items-center gap-2">
            {(selectedThread || showNewThread) && (
              <button 
                className="btn btn-sm btn-link text-white p-0 text-decoration-none"
                onClick={backToList}
                style={{ fontSize: '1.2rem' }}
              >
                ←
              </button>
            )}
            <h5 className="m-0">
              {selectedThread 
                ? getThreadParticipantNames(selectedThread)
                : showNewThread 
                ? 'New Message' 
                : 'Messages'}
            </h5>
          </div>
          <button 
            className="btn btn-sm btn-outline-light"
            onClick={handleClose}
          >
            Close
          </button>
        </div>

        {/* Body */}
        <div className="messages-modal__body">
          {/* Sidebar - Thread List */}
          <aside 
            className={`messages-modal__sidebar ${selectedThread ? 'thread-selected' : ''}`}
            style={{ display: selectedThread ? 'none' : 'block' }}
          >
            {!showNewThread && (
              <>
                <div className="p-3">
                  <button 
                    className="btn btn-primary w-100"
                    onClick={() => setShowNewThread(true)}
                  >
                    + New Message
                  </button>
                </div>

                {loadingThreads && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-light" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {threadsError && (
                  <div className="alert alert-danger mx-3" role="alert">
                    {threadsError}
                  </div>
                )}

                {!loadingThreads && !threadsError && threads.length === 0 && (
                  <div className="text-center text-muted py-5">
                    <p className="mb-2">No messages yet</p>
                    <p className="small">Click "New Message" to start a conversation</p>
                  </div>
                )}

                {!loadingThreads && threads.length > 0 && (
                  <div className="list-group list-group-flush">
                    {threads.map(thread => {
                      const displayName = getThreadParticipantNames(thread);
                      const lastMessage = thread?.lastMessage || thread?.last_message || '';
                      const unreadCount = Number(thread?.unreadCount || thread?.unread_count || 0);
                      const lastAt = thread?.lastAt || thread?.last_at;

                      return (
                        <button
                          key={thread.id}
                          className="thread-item list-group-item list-group-item-action"
                          onClick={() => openThread(thread)}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <strong className="text-truncate flex-grow-1">{displayName}</strong>
                            {lastAt && (
                              <small className="text-muted ms-2" style={{ whiteSpace: 'nowrap' }}>
                                {formatTimestamp(lastAt)}
                              </small>
                            )}
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="text-muted small text-truncate flex-grow-1">
                              {lastMessage || 'No messages yet'}
                            </div>
                            {unreadCount > 0 && (
                              <span className="badge bg-primary rounded-pill ms-2">
                                {unreadCount}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* New Message View */}
            {showNewThread && (
              <div className="p-3">
                <div className="mb-3">
                  <label className="form-label text-white">To:</label>
                  <input
                    type="text"
                    className="form-control bg-dark text-white border-secondary"
                    placeholder="Search users..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>

                {selectedUsers.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {selectedUsers.map(user => {
                        const uid = Number(user?.id || user?.userid || user?.userId || 0);
                        const name = user?.displayName || user?.username || user?.email || 'Unknown';
                        return (
                          <span key={uid} className="badge bg-primary d-flex align-items-center gap-1">
                            {name}
                            <button
                              type="button"
                              className="btn-close btn-close-white"
                              style={{ fontSize: '0.6rem' }}
                              onClick={() => toggleUserSelection(user)}
                            />
                          </span>
                        );
                      })}
                    </div>
                    <button
                      className="btn btn-success w-100"
                      onClick={handleCreateThread}
                      disabled={creatingThread}
                    >
                      {creatingThread ? 'Creating...' : 'Start Conversation'}
                    </button>
                  </div>
                )}

                {searchingUsers && (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-light" role="status">
                      <span className="visually-hidden">Searching...</span>
                    </div>
                  </div>
                )}

                {!searchingUsers && userSearchResults.length > 0 && (
                  <div className="list-group">
                    {userSearchResults.map(user => {
                      const uid = Number(user?.id || user?.userid || user?.userId || 0);
                      const name = user?.displayName || user?.username || 'Unknown';
                      const email = user?.email || '';
                      const isSelected = selectedUsers.some(u => {
                        const selectedId = Number(u?.id || u?.userid || u?.userId || 0);
                        return selectedId === uid;
                      });

                      return (
                        <button
                          key={uid}
                          className={`search-result-item list-group-item list-group-item-action bg-dark text-white ${isSelected ? 'active' : ''}`}
                          onClick={() => toggleUserSelection(user)}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isSelected}
                              readOnly
                            />
                            <div className="flex-grow-1">
                              <div className="fw-semibold">{name}</div>
                              {email && <div className="small text-muted">{email}</div>}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {!searchingUsers && userSearchQuery.length >= 2 && userSearchResults.length === 0 && (
                  <div className="text-center text-muted py-3">
                    No users found matching "{userSearchQuery}"
                  </div>
                )}
              </div>
            )}
          </aside>

          {/* Main Content - Message Thread */}
          <main 
            className={`messages-modal__content ${selectedThread ? 'thread-selected' : ''}`}
            style={{ display: selectedThread ? 'flex' : 'none' }}
          >
            {selectedThread && (
              <>
                {loadingMessages && (
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-light" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {messagesError && (
                  <div className="alert alert-danger m-3" role="alert">
                    {messagesError}
                  </div>
                )}

                {!loadingMessages && !messagesError && (
                  <>
                    {/* Messages Area */}
                    <div 
                      ref={scrollContainerRef}
                      className="flex-grow-1 p-3"
                      style={{ overflowY: 'auto', overflowX: 'hidden' }}
                    >
                      {messages.length === 0 && (
                        <div className="text-center text-muted py-5">
                          <p>No messages yet</p>
                          <p className="small">Start the conversation below</p>
                        </div>
                      )}

                      {messages.map((msg, index) => {
                        const senderId = Number(msg?.senderId || msg?.sender_id || 0);
                        const isMine = senderId === myUserId;
                        const text = msg?.text || msg?.body || msg?.content || '';
                        const timestamp = msg?.createdAt || msg?.created_at || msg?.timestamp;
                        const isOptimistic = msg?.isOptimistic;

                        return (
                          <div
                            key={msg.id || index}
                            className={`mb-3 d-flex ${isMine ? 'justify-content-end' : 'justify-content-start'}`}
                          >
                            <div
                              className={`rounded px-3 py-2 ${
                                isMine 
                                  ? 'bg-primary text-white' 
                                  : 'bg-dark text-white border border-secondary'
                              }`}
                              style={{ 
                                maxWidth: '75%',
                                opacity: isOptimistic ? 0.6 : 1
                              }}
                            >
                              <div style={{ wordWrap: 'break-word' }}>{text}</div>
                              {timestamp && (
                                <div 
                                  className={`small mt-1 ${isMine ? 'text-white-50' : 'text-muted'}`}
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  {formatTimestamp(timestamp)}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Compose Area */}
                    <div className="border-top border-secondary p-3">
                      <form onSubmit={handleSendMessage}>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control bg-dark text-white border-secondary"
                            placeholder="Type a message..."
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            disabled={sending}
                            style={{ fontSize: '0.95rem' }}
                          />
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!messageText.trim() || sending}
                          >
                            {sending ? 'Sending...' : 'Send'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}